import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { fetchTakerBySlug } from './services/appointmentService';
import { useBookingFlow } from './hooks/useBookingFlow';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultAppointmentSlug } from '../../config/api';
import { useClientCountry } from '../../hooks/useClientCountry';
import BookingShell from './components/BookingShell';
import BookingStepper from './components/BookingStepper';
import StepChooseTime from './components/StepChooseTime';
import StepYourDetails from './components/StepYourDetails';
import StepPayment from './components/StepPayment';
import StepConfirmation from './components/StepConfirmation';
import BookingNavFooter from './components/BookingNavFooter';
import type { AppointmentTaker } from '../marketplace/types';

export const AppointmentBookingPage: React.FC = () => {
  const { slug: urlSlug } = useParams<{ slug: string }>();
  const { country } = useClientCountry();
  const slug = urlSlug ?? getDefaultAppointmentSlug(country);
  const navigate = useNavigate();
  const location = useLocation();
  const { client } = useAuth();

  const bookingReturnPath =
    (location.state as { bookingReturnPath?: string } | null)?.bookingReturnPath ?? '/appointments';
  const bookingReturnLabel = bookingReturnPath === '/' ? 'Home' : 'All consultants';

  const [taker, setTaker] = useState<AppointmentTaker | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingTaker, setLoadingTaker] = useState(true);
  const [showDetailsErrors, setShowDetailsErrors] = useState(false);

  const flow = useBookingFlow(taker);
  const { state, dispatch, prefillFromClient, goNext, goBack } = flow;

  // Load taker from URL slug
  useEffect(() => {
    setLoadingTaker(true);
    setLoadError(null);
    setTaker(null);
    fetchTakerBySlug(slug)
      .then((t) => setTaker(t))
      .catch(() => setLoadError('Consultant not found or unavailable.'))
      .finally(() => setLoadingTaker(false));
  }, [slug]);

  // Prefill from auth
  useEffect(() => {
    if (client) {
      prefillFromClient({
        name: client.name,
        email: client.email,
        phone: client.phone,
        country_code: client.country_code,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.client_id]);

  if (loadError || (!loadingTaker && !taker)) {
    return (
      <BookingShell>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError || 'Consultant not found'}</Alert>
        <Button onClick={() => navigate(bookingReturnPath)} startIcon={<ArrowBackIcon />}>
          {bookingReturnLabel}
        </Button>
      </BookingShell>
    );
  }

  const handleNext = () => {
    if (state.activeStep === 1) setShowDetailsErrors(true);
    goNext();
  };

  const handleGoBack = () => {
    setShowDetailsErrors(false);
    goBack();
  };

  const isProcessingPayment = state.paymentStatus === 'pending';
  const showNavFooter = taker && !loadingTaker && !isProcessingPayment;

  const backLabel =
    state.activeStep === 0 || state.activeStep === 3 ? bookingReturnLabel : 'Back';

  const handleBack = () => {
    if (state.activeStep === 0 || state.activeStep === 3) {
      navigate(bookingReturnPath);
      return;
    }
    handleGoBack();
  };

  const navFooter = showNavFooter ? (
    <BookingNavFooter
      backLabel={backLabel}
      onBack={handleBack}
      showNext={state.activeStep < 2}
      nextLabel={state.activeStep === 1 ? 'Continue to Payment' : 'Next'}
      onNext={handleNext}
      nextDisabled={!state.selectedSlot && state.activeStep === 0}
      nextHint={
        !state.selectedSlot && state.activeStep === 0
          ? 'Select a time slot to continue'
          : undefined
      }
    />
  ) : undefined;

  return (
    <BookingShell displayName={taker?.displayName} loading={loadingTaker} footer={navFooter}>
      <BookingStepper activeStep={state.activeStep} />

      {state.stepError && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} onClose={() => dispatch({ type: 'SET_STEP_ERROR', error: null })}>
          {state.stepError}
        </Alert>
      )}

      {/* Step 0: Choose Time */}
      {state.activeStep === 0 && taker && (
        <StepChooseTime
          taker={taker}
          appointmentType={state.appointmentType}
          date={state.date}
          selectedSlot={state.selectedSlot}
          onTypeChange={(type) => dispatch({ type: 'SET_TYPE', appointmentType: type })}
          onDateChange={(d) => dispatch({ type: 'SET_DATE', date: d })}
          onSlotSelect={(s) => dispatch({ type: 'SET_SLOT', slot: s })}
        />
      )}

      {/* Step 1: Your Details */}
      {state.activeStep === 1 && (
        <StepYourDetails
          details={state.bookerDetails}
          onChange={(partial) => dispatch({ type: 'SET_BOOKER', bookerDetails: partial })}
          showErrors={showDetailsErrors}
        />
      )}

      {/* Step 2: Payment */}
      {state.activeStep === 2 && taker && state.selectedSlot && (
        <StepPayment
          taker={taker}
          appointmentType={state.appointmentType}
          selectedSlot={state.selectedSlot}
          bookerDetails={state.bookerDetails}
          pricing={state.pricing}
          coupon={state.coupon}
          paymentStatus={state.paymentStatus}
          paymentError={state.paymentError}
          slug={slug ?? ''}
          onCouponCodeChange={(code) => dispatch({ type: 'SET_COUPON_CODE', code })}
          onApplyCoupon={flow.applyPromoCode}
          onPayNow={flow.handlePayNow}
          onBookFree={flow.handleBookFree}
        />
      )}

      {/* Step 3: Confirmation */}
      {state.activeStep === 3 && taker && (
        <StepConfirmation
          taker={taker}
          confirmation={state.confirmationData}
          paymentStatus={state.paymentStatus}
          paymentError={state.paymentError}
          onRetry={flow.retryCreateBooking}
        />
      )}
    </BookingShell>
  );
};
